// Short synthesized audio cues for the AI participant (ideas.md "AI Audio Cues").
// Two distinct, subtle blips: one when Claude *starts processing* (a pending card
// opens) and one when a card *completes*. Synthesized with the Web Audio API so
// there are no external assets to fetch (keeps the single-static-bundle rule).
//
// bridgeClient.ts (non-React) fires these; a mute toggle in the transcript footer
// reads/writes the persisted flag. Calls are no-ops while muted or if Web Audio
// is unavailable.

const STORAGE_KEY = 'convariance.aiAudioCues.muted'

function readMuted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

let muted = readMuted()

export function isCuesMuted(): boolean {
  return muted
}

export function setCuesMuted(value: boolean): void {
  muted = value
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
  } catch {
    // private mode / blocked storage — keep the in-memory flag, just don't persist
  }
}

// Lazily created and reused; the user has already gestured (pressed Start) by the
// time a cue plays, so the context is allowed to run.
let ctx: AudioContext | null = null

function audioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  if (!ctx) ctx = new Ctor()
  if (ctx.state === 'suspended') void ctx.resume()
  return ctx
}

// One soft sine "note" with a quick attack + exponential decay, scheduled at
// `offset` seconds from now. Gain is kept low so the cue reads as a gentle blip;
// `peak` lets a more deliberate cue (the floor-claim PING) sit a touch louder.
function note(
  ac: AudioContext,
  freq: number,
  offset: number,
  dur: number,
  peak = 0.12
): void {
  const t0 = ac.currentTime + offset
  const osc = ac.createOscillator()
  const gain = ac.createGain()
  osc.type = 'sine'
  osc.frequency.value = freq
  gain.gain.setValueAtTime(0.0001, t0)
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.012)
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
  osc.connect(gain).connect(ac.destination)
  osc.start(t0)
  osc.stop(t0 + dur + 0.02)
}

// Claude starts working: a single low, soft tick — unobtrusive, says "I'm on it".
export function playStartCue(): void {
  if (muted) return
  const ac = audioCtx()
  if (!ac) return
  note(ac, 392, 0, 0.14) // G4
}

// A card lands: a brighter two-note rising chime — clearly different from start.
export function playCompleteCue(): void {
  if (muted) return
  const ac = audioCtx()
  if (!ac) return
  note(ac, 659, 0, 0.12) // E5
  note(ac, 988, 0.1, 0.18) // B5
}

// The AI is about to SPEAK (auto-speak floor-claim). A distinct, slightly louder
// two-note "attention" rise, fired the instant a claim-window opens — BEFORE the
// voice has even loaded — so the room notices and yields rather than talking over
// the AI. Deliberately NOT gated by the cue-mute: it's tied to auto-speak, which
// has its own toggle, and a silent floor-claim would defeat the point.
export function playPingCue(): void {
  const ac = audioCtx()
  if (!ac) return
  note(ac, 587, 0, 0.1, 0.2) // D5
  note(ac, 880, 0.09, 0.18, 0.2) // A5
}
